import { Stethoscope, Wrench, Briefcase, Rocket } from "lucide-react";
import { useLeadModal } from "../../context/LeadModalContext";

const audiences = [
    {
        Icon: Stethoscope,
        title: "Healthcare Practices",
        sub: "Med spas, chiropractors, dental, mental health, physical therapy",
        desc: "Intake bots, scheduling agents, patient FAQ assistants, transcription workflows. Built with HIPAA-conscious architecture from day one.",
    },
    {
        Icon: Wrench,
        title: "Local Service Businesses",
        sub: "HVAC, contractors, real estate, home services, professional services",
        desc: "Lead qualification bots, quote generators, dispatch automations, review-request flows. Built for the way you actually run your business.",
    },
    {
        Icon: Briefcase,
        title: "Solo Professionals & Coaches",
        sub: "Consultants, advisors, attorneys, accountants, agencies under 10 people",
        desc: "Client intake, proposal generators, admin automations, internal AI copilots. Reclaim 10+ hours a week.",
    },
    {
        Icon: Rocket,
        title: "SaaS & Indie Founders",
        sub: "Solopreneurs shipping products, post-MVP teams needing infrastructure",
        desc: "Custom agent integrations, internal dashboards, churn-prevention bots, support automation. From founder to founder.",
    },
];

export default function WhoWeBuildFor() {
    const { openModal } = useLeadModal();

    return (
        <section
            id="who-we-build-for"
            data-testid="who-we-build-for-section"
            className="relative py-24 sm:py-32 scroll-mt-nav overflow-hidden"
        >
            <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-[#1d4ed8]/10 blur-[140px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
                <div className="text-center max-w-3xl mx-auto mb-14">
                    <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00f0ff]">
                        Built for
                    </span>
                    <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mt-4 leading-[1.05]">
                        If your business runs on{" "}
                        <span className="text-gradient-cyan">repetitive work</span>, we can
                        automate it.
                    </h2>
                    <p className="text-lg text-[#94a3b8] mt-6 leading-relaxed">
                        We specialize in custom AI builds for businesses that don&apos;t have a
                        CTO — and don&apos;t want one. These are the four kinds of operators we
                        work with most:
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
                    {audiences.map(({ Icon, title, sub, desc }, i) => (
                        <article
                            key={title}
                            data-testid={`audience-card-${i + 1}`}
                            className="group relative rounded-2xl bg-[#0a1325] border border-white/10 p-8 hover:border-[#00f0ff]/40 hover:-translate-y-1 transition-all duration-500 overflow-hidden"
                        >
                            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#00f0ff]/0 group-hover:bg-[#00f0ff]/10 blur-3xl transition-all duration-500" />
                            <div className="relative">
                                <div className="w-12 h-12 rounded-lg bg-[#00f0ff]/10 border border-[#00f0ff]/30 flex items-center justify-center mb-5">
                                    <Icon className="w-5.5 h-5.5 text-[#00f0ff]" strokeWidth={2} />
                                </div>
                                <h3 className="font-heading text-2xl font-bold tracking-tight text-white">
                                    {title}
                                </h3>
                                <p className="text-xs uppercase tracking-wider text-[#00f0ff]/70 font-bold mt-2 mb-4">
                                    {sub}
                                </p>
                                <p className="text-sm text-[#94a3b8] leading-relaxed">{desc}</p>
                            </div>
                        </article>
                    ))}
                </div>

                <p className="text-center text-sm italic text-[#64748b] mt-12 max-w-2xl mx-auto">
                    Not on this list?{" "}
                    <button
                        onClick={() => openModal("who-we-build-for")}
                        data-testid="audience-cta-blueprint"
                        className="not-italic font-medium text-[#94a3b8] underline decoration-[#00f0ff]/40 underline-offset-4 hover:text-[#00f0ff] transition-colors"
                    >
                        Book a Blueprint Call
                    </button>{" "}
                    anyway — if we can&apos;t help, we&apos;ll point you to someone who can.
                </p>
            </div>
        </section>
    );
}
